'use client'

import type { ChangeEvent } from 'react'
import { type FC, useState } from 'react'
import styles from './SaveAddressAs.module.scss'
import { useLocalisation } from '@/tools/i18n/client'
import { Chip, TextField } from '@/tools/ui'
import type { UserAddress, UserAddressKind, UserAddressTag } from '@/user/address-book/entities'
import { ADDRESS_TAG_OPTIONS, ADDRESS_TAG_T_KEYS } from '@/user/address-book/ui/constants'

interface Props {
  onTagChange: (tag: string) => void
  addressKind: UserAddressKind
  addressTag: UserAddress['tag']
  isAddressTagMissing: boolean
}

export const SaveAddressAs: FC<Props> = ({
  onTagChange: handleTagChange,
  addressKind,
  addressTag,
  isAddressTagMissing: isAddressTaMissing,
}) => {
  const { t } = useLocalisation()

  const options = ADDRESS_TAG_OPTIONS[addressKind]
  const isStandardAddressTag = options.includes(addressTag as UserAddressTag)
  const initialChipSelected = addressTag ? (isStandardAddressTag ? addressTag : 'CUSTOM') : ''
  const initialCustomTag = addressTag && !isStandardAddressTag ? addressTag : ''
  const addressTagError = isAddressTaMissing ? t('common.required') : ''

  const [chipSelected, setChipSelected] = useState<string>(initialChipSelected)
  const [customTag, setCustomTag] = useState<string>(initialCustomTag)
  const isCustomSelected = chipSelected === 'CUSTOM'

  const handleClick = (option: string) => {
    setChipSelected(option)
    if (option === 'CUSTOM') {
      handleTagChange(customTag)
    } else {
      handleTagChange(option)
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newCustomTag = e.target.value
    setCustomTag(newCustomTag.trim())
    handleTagChange(newCustomTag.trim())
  }

  return (
    <div className={styles.saveAddressAs}>
      <h3 className={styles.title}>{t('address-details.save-address-as.title')}</h3>
      <p className={styles.subtitle}>{t('address-details.save-address-as.subtitle')}</p>
      <div className={styles.chipContainer}>
        {options.map((option) => {
          const isSelected = chipSelected === option
          return (
            <Chip key={option} selected={isSelected} onClick={() => handleClick(option)}>
              <span className={isSelected ? styles.boldText : styles.normalText}>
                {t(ADDRESS_TAG_T_KEYS[option])}
              </span>
            </Chip>
          )
        })}
        <Chip selected={isCustomSelected} onClick={() => handleClick('CUSTOM')}>
          <span className={isCustomSelected ? styles.boldText : styles.normalText}>
            {t('address-details.save-address-as.custom')}
          </span>
        </Chip>
      </div>
      {isCustomSelected && (
        <TextField
          aria-label={t('address-details.save-address-as.custom-label')}
          error={addressTagError}
          label={t('address-details.save-address-as.custom-label')}
          value={customTag}
          onChange={handleChange}
        />
      )}
    </div>
  )
}
